import { useEffect, useState } from 'react';
import type { Project } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { SwotMatrixPanel } from '@/features/pilotage/SwotMatrixPanel';
import { StrategicDashboardPanel } from '@/features/pilotage/StrategicDashboardPanel';
import { ResourcesPanel } from '@/features/pilotage/ResourcesPanel';
import { MarketingPilotageHub } from '@/features/pilotage/MarketingPilotageHub';
import { CollapsibleCard } from '@/features/ui/CollapsibleCard';
import { useToast } from '@/features/ui/toast/useToast';

type Hub = 'menu' | 'strategique' | 'marketing' | 'ressources';

export function PilotagePage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [hub, setHub] = useState<Hub>('menu');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [vision, setVision] = useState('');
  const [mission, setMission] = useState('');
  const [valuesText, setValuesText] = useState('');
  const [journeySummary, setJourneySummary] = useState('');
  const [currentDecision, setCurrentDecision] = useState('');

  useEffect(() => {
    if (!profile) return;
    void kuunda
      .from('projects')
      .select('*')
      .eq('user_id', profile.id)
      .then(({ data }) => {
        const list = (data ?? []) as Project[];
        setProjects(list);
        if (list[0]) setProjectId(list[0].id);
      });
  }, [profile]);

  // Vision / mission / valeurs : niveau abonné (tous projets)
  useEffect(() => {
    if (!profile) return;
    void (async () => {
      const { data } = await kuunda
        .from('vision_mission_values')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();
      if (data) {
        setCompanyName(data.company_name ?? '');
        setDirectorName(data.director_name ?? '');
        setVision(data.vision ?? '');
        setMission(data.mission ?? '');
        setValuesText(data.values_text ?? '');
      } else {
        setCompanyName('');
        setDirectorName('');
        setVision('');
        setMission('');
        setValuesText('');
      }
    })();
  }, [profile]);

  useEffect(() => {
    if (!projectId || !profile) return;
    void (async () => {
      const { data } = await kuunda
        .from('maturation_decisions')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();
      if (data) {
        setJourneySummary(data.journey_summary ?? '');
        setCurrentDecision(data.current_decision ?? '');
      } else {
        setJourneySummary('');
        setCurrentDecision('');
      }
    })();
  }, [projectId, profile]);

  async function saveVision() {
    if (!profile) return;
    const { error: err } = await kuunda.from('vision_mission_values').upsert(
      {
        user_id: profile.id,
        project_id: null,
        company_name: companyName,
        director_name: directorName,
        vision,
        mission,
        values_text: valuesText,
      },
      { onConflict: 'user_id' },
    );
    if (err) toast.error(err.message);
    else toast.success('Vision / mission / valeurs enregistrées (tous projets).');
  }

  async function saveMaturation() {
    if (!profile || !projectId) return;
    const { error: err } = await kuunda.from('maturation_decisions').upsert(
      {
        user_id: profile.id,
        project_id: projectId,
        journey_summary: journeySummary,
        current_decision: currentDecision,
      },
      { onConflict: 'project_id' },
    );
    if (err) toast.error(err.message);
    else toast.success('Décision de maturation enregistrée.');
  }

  const selectedProject = projects.find((p) => p.id === projectId);

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Pilotage stratégique, marketing / vente et ressources.</p>
        </div>
        <div className="u-actions">
          {hub !== 'marketing' ? (
            <div className="u-filters">
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {hub !== 'menu' ? (
            <button type="button" className="btn btn-ghost" onClick={() => setHub('menu')}>
              ← Retour hub
            </button>
          ) : null}
        </div>
      </div>

      {hub === 'menu' ? (
        <div>
          <h3 className="u-section-title">Modules</h3>
          <div className="u-modules-stack">
            <button type="button" className="u-module u-module--navy" onClick={() => setHub('strategique')}>
              <div className="u-module-top">
                <span className="u-module-step">01</span>
                <div className="u-module-icon navy">P</div>
              </div>
              <h3>Pilotage stratégique</h3>
              <p>Vision, SWOT, matrice, tableau de bord et synthèse.</p>
              <span className="u-module-cta">Ouvrir</span>
            </button>
            <button type="button" className="u-module u-module--green" onClick={() => setHub('marketing')}>
              <div className="u-module-top">
                <span className="u-module-step">02</span>
                <div className="u-module-icon green">M</div>
              </div>
              <h3>Pilotage marketing et vente</h3>
              <p>CRM, CA, fidélisation, marketplace et assistant IA.</p>
              <span className="u-module-cta">Ouvrir</span>
            </button>
            <button type="button" className="u-module u-module--orange" onClick={() => setHub('ressources')}>
              <div className="u-module-top">
                <span className="u-module-step">03</span>
                <div className="u-module-icon orange">R</div>
              </div>
              <h3>Pilotage des ressources</h3>
              <p>Financier, humain et matériel par projet.</p>
              <span className="u-module-cta">Ouvrir</span>
            </button>
          </div>
        </div>
      ) : null}

      {hub === 'marketing' ? <MarketingPilotageHub /> : null}

      {hub === 'ressources' ? (
        projectId ? (
          <ResourcesPanel projectId={projectId} />
        ) : (
          <p className="u-empty">Sélectionnez un projet.</p>
        )
      ) : null}

      {hub === 'strategique' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <CollapsibleCard
            title="Vision, mission, valeurs"
            badge="Entreprise"
            collapsedHint="Section repliée — dépliez pour renseigner vision, mission et valeurs."
          >
            <p className="muted u-synth-section-desc">
              Informations d’entreprise partagées pour tous vos projets.
            </p>
            <div className="grid-2">
              <div className="field">
                <label>Nom de l’entreprise</label>
                <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              </div>
              <div className="field">
                <label>Dirigeant responsable</label>
                <input value={directorName} onChange={(e) => setDirectorName(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label>Vision (5 ans)</label>
              <textarea value={vision} onChange={(e) => setVision(e.target.value)} />
            </div>
            <div className="field">
              <label>Mission</label>
              <textarea value={mission} onChange={(e) => setMission(e.target.value)} />
            </div>
            <div className="field">
              <label>Valeurs</label>
              <textarea value={valuesText} onChange={(e) => setValuesText(e.target.value)} />
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void saveVision()}>
              Enregistrer
            </button>
          </CollapsibleCard>

          <CollapsibleCard
            title="Décision de maturation"
            badge={selectedProject?.title || 'Projet'}
            collapsedHint="Section repliée — dépliez pour la synthèse du parcours et la décision."
          >
            <p className="muted u-synth-section-desc">
              Synthèse du parcours et décision actuelle (liée au projet).
            </p>
            <div className="field">
              <label>Synthèse du parcours</label>
              <textarea
                rows={4}
                value={journeySummary}
                onChange={(e) => setJourneySummary(e.target.value)}
                placeholder="Ce que vous avez appris depuis la conception…"
              />
            </div>
            <div className="field">
              <label>Décision actuelle</label>
              <select value={currentDecision} onChange={(e) => setCurrentDecision(e.target.value)}>
                <option value="">— Choisir —</option>
                <option value="continue">Poursuivre le déploiement</option>
                <option value="pivot">Pivoter l’offre / le marché</option>
                <option value="pause">Mettre en pause</option>
                <option value="stop">Arrêter le projet</option>
                <option value="scale">Scaler / accélérer</option>
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!projectId}
              onClick={() => void saveMaturation()}
            >
              Enregistrer la décision
            </button>
          </CollapsibleCard>

          {projectId ? (
            <>
              <div className="u-card">
                <SwotMatrixPanel projectId={projectId} />
              </div>
              <div className="u-card">
                <StrategicDashboardPanel projectId={projectId} projectTitle={selectedProject?.title} />
              </div>
            </>
          ) : (
            <p className="u-empty">Sélectionnez un projet pour SWOT, matrice et synthèse.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
